import { useCallback, useEffect, useState } from 'react'

import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import { GUIDED_INTAKE_EVENTS } from '../../constants'
import {
  canHaveSupplierWidget,
  type ConversationCatalogItemComponent,
} from '../../value-objects/ConversationTurnItem'
import { deriveSourceChannel } from '../../value-objects/ConversationTurnItem/deriveSourceChannel'
import {
  CatalogItemDetailsStatuses,
  useCatalogItemDetails,
  type CatalogItemDetailsState,
  type SendTechnicalMessage,
} from '../useCatalogItemDetails'

const DEFAULT_QUANTITY = 1

const NO_SELECTED_ITEM_DETAILS: CatalogItemDetailsState = {
  status: CatalogItemDetailsStatuses.IDLE,
  detailSections: [],
}

export interface UseCatalogItemCartResult {
  selectedItem: Nullable<ConversationCatalogItemComponent>
  selectedItemDetails: CatalogItemDetailsState
  quantity: number
  estimatedTotal: number
  selectCatalogItem: (item: ConversationCatalogItemComponent) => void
  collapse: () => void
  changeQuantity: (quantity: number) => void
  addToRequest: () => void
  quantityOf: (item: ConversationCatalogItemComponent) => number
  changeItemQuantity: (
    item: ConversationCatalogItemComponent,
    quantity: number
  ) => void
  addItemToRequest: (item: ConversationCatalogItemComponent) => void
  askFollowUp: (question: string) => void
}

export const useCatalogItemCart = (
  sendMessage: (message: string) => boolean,
  conversationId: Nullable<string>,
  sendTechnicalMessage: SendTechnicalMessage
): UseCatalogItemCartResult => {
  const intl = useProcurifyIntl()
  const { detailsOf, requestItemDetails } =
    useCatalogItemDetails(sendTechnicalMessage)
  const [selectedItem, setSelectedItem] =
    useState<Nullable<ConversationCatalogItemComponent>>(null)
  const [quantitiesById, setQuantitiesById] = useState<Record<string, number>>(
    {}
  )

  const quantityOf = useCallback(
    (item: ConversationCatalogItemComponent) =>
      quantitiesById[item.itemId] ?? item.quantity ?? DEFAULT_QUANTITY,
    [quantitiesById]
  )

  const quantity = selectedItem ? quantityOf(selectedItem) : DEFAULT_QUANTITY
  const estimatedTotal = selectedItem ? selectedItem.price * quantity : 0

  const selectedItemDetails = selectedItem
    ? detailsOf(selectedItem.itemId)
    : NO_SELECTED_ITEM_DETAILS

  const supplierWidgetItemId =
    selectedItem && canHaveSupplierWidget(selectedItem)
      ? selectedItem.itemId
      : null

  useEffect(() => {
    if (supplierWidgetItemId !== null) requestItemDetails(supplierWidgetItemId)
  }, [requestItemDetails, supplierWidgetItemId])

  const selectCatalogItem = useCallback(
    (item: ConversationCatalogItemComponent) => setSelectedItem(item),
    []
  )

  const collapse = useCallback(() => setSelectedItem(null), [])

  const changeItemQuantity = useCallback(
    (item: ConversationCatalogItemComponent, next: number) => {
      setQuantitiesById((prev) => ({ ...prev, [item.itemId]: next }))
    },
    []
  )

  const changeQuantity = useCallback(
    (next: number) => {
      if (!selectedItem) return
      changeItemQuantity(selectedItem, next)
    },
    [changeItemQuantity, selectedItem]
  )

  const addItemToRequest = useCallback(
    (item: ConversationCatalogItemComponent) => {
      const didSend = sendMessage(
        intl.formatMessage(messages.addToRequest, {
          title: item.title,
          quantity: quantityOf(item),
        })
      )
      if (!didSend) return

      window.analytics?.track(GUIDED_INTAKE_EVENTS.ITEM_SELECTED, {
        conversation_id: conversationId,
        source_channel: deriveSourceChannel(item.vendor),
        rank_position: item.rank ?? null,
      })
      setSelectedItem(null)
    },
    [conversationId, intl, quantityOf, sendMessage]
  )

  const addToRequest = useCallback(() => {
    if (!selectedItem) return
    addItemToRequest(selectedItem)
  }, [addItemToRequest, selectedItem])

  const askFollowUp = useCallback(
    (question: string) => {
      if (!selectedItem) return

      const trimmedQuestion = question.trim()
      if (!trimmedQuestion) return

      const didSend = sendMessage(trimmedQuestion)
      if (didSend) setSelectedItem(null)
    },
    [selectedItem, sendMessage]
  )

  return {
    selectedItem,
    selectedItemDetails,
    quantity,
    estimatedTotal,
    selectCatalogItem,
    collapse,
    changeQuantity,
    addToRequest,
    quantityOf,
    changeItemQuantity,
    addItemToRequest,
    askFollowUp,
  }
}
